import React from 'react';
import { useSelector } from 'react-redux';
import LogoDark from '../../assets/logo-dark.svg';
import LogoWhite from '../../assets/logo-white.svg';

const WalkingSpinner = ({
  size = 96,
  label = 'Loading…',
  fullScreen = false,
  className = '',
}) => {
  const isDark = useSelector((state) => state.commonReducer?.isDark);
  const logo = isDark ? LogoWhite : LogoDark;
  const uid = React.useId().replace(/:/g, '');

  const inner = (
    <div
      role="status"
      aria-label={label}
      className={className}
      style={{
        width: size * 1.8,
        height: size * 1.45,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-end',
      }}
    >
      <style>{`
        @keyframes tw-walk-body-${uid} {
          0%   { transform: translateY(0)   rotate(-7deg) }
          25%  { transform: translateY(-6px) rotate(-3deg) }
          50%  { transform: translateY(0)   rotate(7deg) }
          75%  { transform: translateY(-6px) rotate(3deg) }
          100% { transform: translateY(0)   rotate(-7deg) }
        }
        @keyframes tw-walk-foot-l-${uid} {
          0%   { opacity: 0;   transform: translateX(14px) scale(0.6) }
          15%  { opacity: 1;   transform: translateX(8px)  scale(1) }
          60%  { opacity: 0.6; transform: translateX(-10px) scale(0.9) }
          100% { opacity: 0;   transform: translateX(-20px) scale(0.6) }
        }
        @keyframes tw-walk-foot-r-${uid} {
          0%   { opacity: 0;   transform: translateX(14px) scale(0.6) }
          50%  { opacity: 0;   transform: translateX(14px) scale(0.6) }
          65%  { opacity: 1;   transform: translateX(8px)  scale(1) }
          100% { opacity: 0.3; transform: translateX(-10px) scale(0.9) }
        }
        @keyframes tw-walk-shadow-${uid} {
          0%, 50%, 100% { transform: scaleX(1);    opacity: 0.18 }
          25%, 75%      { transform: scaleX(0.75); opacity: 0.32 }
        }
      `}</style>

      <div
        style={{
          width: size,
          height: size,
          animation: `tw-walk-body-${uid} 0.9s cubic-bezier(.5,.05,.5,.95) infinite`,
          transformOrigin: '50% 85%',
        }}
      >
        <img
          src={logo}
          alt="Thisway"
          width="100%"
          height="100%"
          style={{ display: 'block', width: '100%', height: '100%' }}
        />
      </div>

      <div
        style={{
          position: 'relative',
          width: size * 1.5,
          height: 18,
          marginTop: 4,
        }}
      >
        <div
          className="bg-slate-900 dark:bg-slate-100"
          style={{
            position: 'absolute',
            left: '50%',
            top: 2,
            width: size * 0.5,
            height: 6,
            borderRadius: '50%',
            transform: 'translateX(-50%)',
            animation: `tw-walk-shadow-${uid} 0.9s ease-in-out infinite`,
            filter: 'blur(2px)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: 10,
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: size * 0.18,
            alignItems: 'center',
          }}
        >
          <span
            className="bg-slate-900 dark:bg-slate-100"
            style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              animation: `tw-walk-foot-l-${uid} 0.9s linear infinite`,
            }}
          />
          <span
            className="bg-slate-900 dark:bg-slate-100"
            style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              animation: `tw-walk-foot-r-${uid} 0.9s linear infinite`,
            }}
          />
        </div>
      </div>

      <span className="sr-only">{label}</span>
    </div>
  );

  if (!fullScreen) return inner;

  return (
    <div className="flex h-screen w-full items-center justify-center bg-white dark:bg-slate-900">
      {inner}
    </div>
  );
};

export default WalkingSpinner;
